import React, { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { TooltipProps } from './types';

const VIEWPORT_MARGIN = 8;

export const Tooltip: React.FC<TooltipProps> = ({ content, children, className = '' }) => {
    const [visible, setVisible] = useState(false);
    const [position, setPosition] = useState<{ top: number; left: number; flipBelow: boolean } | null>(null);
    const triggerRef = useRef<HTMLSpanElement>(null);
    const tooltipRef = useRef<HTMLSpanElement>(null);
    const measuredRef = useRef(false);
    const id = useId();

    const updatePosition = () => {
        const rect = triggerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const tooltipRect = tooltipRef.current?.getBoundingClientRect();
        const width = tooltipRect?.width ?? 0;
        const height = tooltipRect?.height ?? 0;

        const rawLeft = rect.left + rect.width / 2;
        const left = width
            ? Math.min(
                  Math.max(rawLeft, width / 2 + VIEWPORT_MARGIN),
                  window.innerWidth - width / 2 - VIEWPORT_MARGIN,
              )
            : rawLeft;

        const fitsAbove = rect.top - height - VIEWPORT_MARGIN >= 0;
        const flipBelow = height > 0 && !fitsAbove;
        const top = flipBelow ? rect.bottom + VIEWPORT_MARGIN : rect.top;

        setPosition({ top, left, flipBelow });
    };

    // Recompute position while visible so scrolling/resizing doesn't leave the tooltip
    // floating over stale coordinates: it's portaled to <body>, so it no longer moves
    // with its trigger automatically once its ancestor's layout shifts.
    useLayoutEffect(() => {
        if (!visible) {
            measuredRef.current = false;
            return;
        }
        updatePosition();
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') setVisible(false);
        };
        // Tapping outside is the only way a touch user (no hover/blur) can dismiss
        // a tooltip opened via the tap-to-toggle affordance.
        const handleOutside = (event: MouseEvent | TouchEvent) => {
            const target = event.target as Node;
            if (triggerRef.current?.contains(target) || tooltipRef.current?.contains(target)) return;
            setVisible(false);
        };
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        window.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('touchstart', handleOutside);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('touchstart', handleOutside);
        };
    }, [visible]);

    // Once the tooltip has actually mounted, refine its position using its real
    // measured size instead of the initial trigger-only estimate.
    useLayoutEffect(() => {
        if (!visible || !position || measuredRef.current) return;
        measuredRef.current = true;
        updatePosition();
    }, [visible, position]);

    if (!content) {
        return typeof children === 'function' ? children(undefined) : children;
    }

    const describedById = visible ? id : undefined;
    const trigger = typeof children === 'function'
        ? children(describedById)
        : React.cloneElement(children, {
              'aria-describedby': describedById ?? children.props['aria-describedby'],
          });

    return (
        <span
            ref={triggerRef}
            className={`inline-flex ${className}`}
            onMouseEnter={() => setVisible(true)}
            onMouseLeave={() => setVisible(false)}
            onFocus={() => setVisible(true)}
            onBlur={() => setVisible(false)}
            // Tap has no hover: this is the only way a touch user opens the tooltip. Set
            // rather than flip — a tap on a labelled control (e.g. a radio) fires focus
            // before click, and a flip would immediately re-close what focus just opened.
            onClick={() => setVisible(true)}
        >
            {trigger}
            {visible && position && createPortal(
                <span
                    ref={tooltipRef}
                    id={id}
                    role="tooltip"
                    style={{
                        position: 'fixed',
                        top: position.top,
                        left: position.left,
                        transform: position.flipBelow
                            ? 'translate(-50%, 0)'
                            : 'translate(-50%, calc(-100% - 8px))',
                    }}
                    className="pointer-events-none z-50 w-max max-w-2xs rounded-md bg-primary-1225 px-2.5 py-1.5 text-xs font-medium text-primary-50 shadow-medium"
                >
                    {content}
                    <span
                        className={
                            position.flipBelow
                                ? 'absolute left-1/2 bottom-full -translate-x-1/2 border-4 border-transparent border-b-primary-1225'
                                : 'absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-primary-1225'
                        }
                    />
                </span>,
                document.body,
            )}
        </span>
    );
};
